import { Calendar, Download, MapPin } from 'lucide-react'
import { personalInfo } from '@/data/personal'
import { education, experience } from '@/data/experience'
import { skillGroups } from '@/data/skills'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useSeo } from '@/hooks/useSeo'
import { trackEvent } from '@/lib/analytics'

const formatRange = (start: string, end?: string, current?: boolean) => {
  const fmt = (d: string) =>
    new Date(d).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
  return `${fmt(start)} - ${current ? 'Present' : end ? fmt(end) : 'Present'}`
}

// Chris doesn't like the per-role tech badge row for now. Data stays intact
// in experience.ts, this just gates the rendering so it's a one-line flip
// to bring back later.
const SHOW_EXPERIENCE_TECH = false

export function ResumePage() {
  useSeo(
    'Resume',
    'Lead Data Scientist working in optimization and machine learning. Experience, education, and skills, with a downloadable PDF.'
  )
  return (
    <div className="container max-w-3xl pb-20 pt-12">
      <header className="flex flex-col gap-4 border-b border-border/40 pb-8 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">
            {personalInfo.name}
          </h1>
          <p className="mt-1 text-muted-foreground">{personalInfo.title}</p>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5" />
              {personalInfo.contact.location}
            </span>
          </div>
        </div>
        <Button asChild>
          <a
            href={personalInfo.resume}
            download={personalInfo.resumeDownloadName}
            onClick={() => trackEvent('resume_download', { source: 'header_button' })}
          >
            <Download className="h-4 w-4" />
            Download PDF
          </a>
        </Button>
      </header>

      <section className="py-10">
        <h2 className="mb-6 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          Experience
        </h2>
        <ol className="space-y-8">
          {experience.map((role) => (
            <li key={role.id}>
              <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
                <div>
                  <h3 className="text-base font-medium">
                    {role.position} &middot;{' '}
                    <span className="text-muted-foreground">{role.company}</span>
                  </h3>
                  <p className="text-sm text-muted-foreground">{role.location}</p>
                </div>
                <p className="inline-flex items-center gap-1 text-xs tabular-nums text-muted-foreground">
                  <Calendar className="h-3 w-3" />
                  {formatRange(role.startDate, role.endDate, role.current)}
                </p>
              </div>
              <ul className="mt-3 list-disc space-y-1.5 pl-5 text-sm text-muted-foreground marker:text-muted-foreground/50">
                {role.description.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
              {SHOW_EXPERIENCE_TECH && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {role.technologies.map((t) => (
                    <Badge
                      key={t}
                      variant="secondary"
                      className="text-[11px] font-normal"
                    >
                      {t}
                    </Badge>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ol>
      </section>

      <section className="border-t border-border/40 py-10">
        <h2 className="mb-6 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          Education
        </h2>
        <ul className="space-y-5">
          {education.map((ed) => (
            <li
              key={ed.id}
              className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between"
            >
              <div>
                <h3 className="text-base font-medium">{ed.institution}</h3>
                <p className="text-sm text-muted-foreground">
                  {ed.degree}, {ed.field}
                </p>
              </div>
              <p className="text-xs tabular-nums text-muted-foreground">
                {formatRange(ed.startDate, ed.endDate)}
              </p>
            </li>
          ))}
        </ul>
      </section>

      <section className="border-t border-border/40 py-10">
        <h2 className="mb-6 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          Skills
        </h2>
        <dl className="space-y-5">
          {skillGroups.map((group) => (
            <div
              key={group.label}
              className="grid grid-cols-1 gap-2 sm:grid-cols-[14rem_1fr] sm:gap-4"
            >
              <dt className="text-sm font-medium">{group.label}</dt>
              <dd className="flex flex-wrap gap-1.5">
                {group.items.map((item) => (
                  <Badge key={item} variant="outline" className="font-normal">
                    {item}
                  </Badge>
                ))}
              </dd>
            </div>
          ))}
        </dl>
      </section>
    </div>
  )
}
